/**
 * Rules for the iOS-style edge swipe (shell/EdgeSwipe.tsx): where it runs,
 * where it may start, and when a drag commits. Kept DOM-light so it can be
 * unit-tested.
 */
import type { Platform } from './viewport.js';

/** Width of the touch-sensitive strip at each screen edge. */
export const EDGE_PX = 20;
/** Fraction of the screen width past which a release commits. */
export const COMMIT_FRACTION = 0.35;
/** Release speed (px/ms) that commits regardless of distance: a flick. */
export const FLICK_SPEED = 0.5;
/** Minimum travel before a flick counts, so a twitch never navigates. */
export const FLICK_MIN_PX = 24;

/**
 * Where the custom gesture runs. Never in the iOS app (WKWebView's native
 * back/forward swipe handles it) and never in an iOS Safari tab (Safari's own
 * edge swipe would fire too). That leaves the installed PWA (standalone, any
 * platform) and non-iOS mobile browsers.
 */
export function edgeSwipeEnabled(env: { native: boolean; standalone: boolean; platform: Platform }): boolean {
  if (env.native) return false;
  return env.standalone || env.platform !== 'ios';
}

/** Gesture starts on these are left alone: they own horizontal drags. */
export const NO_EDGE_SWIPE = [
  '[data-no-edge-swipe]',
  '.slider',
  '[role="slider"]',
  'input[type="range"]',
  '.sched-gridwrap',
  '.sched-grid',
  '.tchart',
  '.uplot',
  '.ht-svgchart',
  'canvas',
].join(', ');

export type Edge = 'left' | 'right';

export function edgeAt(x: number, width: number): Edge | null {
  if (x < EDGE_PX) return 'left';
  if (x > width - EDGE_PX) return 'right';
  return null;
}

/**
 * True when an ancestor of `el` scrolls horizontally and still has room to move
 * the way the finger is going (a finger moving right scrolls content toward
 * its start). The scroller wins over the edge gesture in that case.
 */
export function scrollerWouldMove(el: Element | null, edge: Edge): boolean {
  for (let n: Element | null = el; n && n !== document.body; n = n.parentElement) {
    if (n.scrollWidth <= n.clientWidth + 1) continue;
    const ox = getComputedStyle(n).overflowX;
    if (ox !== 'auto' && ox !== 'scroll') continue;
    if (edge === 'left' ? n.scrollLeft > 0 : n.scrollLeft + n.clientWidth < n.scrollWidth - 1) return true;
  }
  return false;
}

/**
 * Commit decision on release. `travel` is the inward distance (always ≥ 0),
 * `speed` the inward release speed in px/ms (negative = moving back out).
 */
export function shouldCommit(travel: number, width: number, speed: number): boolean {
  if (travel >= width * COMMIT_FRACTION) return speed > -FLICK_SPEED;
  return travel >= FLICK_MIN_PX && speed >= FLICK_SPEED;
}
